import { Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/utils/orderValue';

const countFormatter = new Intl.NumberFormat('en-US');

const formatSource = (source) =>
  source ? source.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Unknown';

// markerFor, when given, is called with each row and returns the sentence to
// show beside that row's name, or nothing. The icon is the same gesture the
// order editor uses to mark a field the AI was unsure about.
const SourceBreakdown = ({ sources = [], loading, error, onRetry, title = 'Orders by Source', verbatim = false,
  valueKey = 'count', formatValue = value => value, valueType = 'count', note = null, markerFor = null }) => {
  const max = sources.reduce((largest, row) => Math.max(largest, row[valueKey]), 0);
  const monetary = valueType === 'money';
  // Sum the displayed monetary rows in cents, not drifting dollar fractions.
  const total = sources.reduce((sum, row) => sum + (monetary
    ? Math.round(row[valueKey] * 100) : row[valueKey]), 0) / (monetary ? 100 : 1);
  const totalText = loading ? '…' : error ? '—'
    : monetary ? formatCurrency(total) : countFormatter.format(total);

  return (
    <Card role="region" aria-label={title}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <CardTitle className="text-lg">{title}</CardTitle>
          <span className="ml-auto text-right text-sm font-bold text-stone-900 break-words min-w-0"
            data-testid="source-breakdown-total">
            Total: {totalText}
          </span>
        </div>
        {note ? (
          <p className="text-xs text-stone-500" data-testid="source-breakdown-note">{note}</p>
        ) : null}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-8 bg-stone-100 rounded animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <p role="alert">Unavailable — <button type="button" onClick={onRetry}>Try again</button></p>
        ) : sources.length === 0 ? (
          <p className="text-sm text-stone-400">No orders for selected range</p>
        ) : (
          <ol className="space-y-2" data-testid="source-breakdown-list">
            {sources.map((row, idx) => {
              const { source, [valueKey]: value } = row;
              const marker = markerFor ? markerFor(row) : null;
              return (
              <li key={source} className="flex items-center gap-3">
                <span className="text-xs text-stone-400 w-4 text-right flex-shrink-0">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center min-w-0">
                      <span className="text-sm font-medium text-stone-800 truncate">
                        {verbatim ? source : formatSource(source)}
                      </span>
                      {marker ? (
                        <span
                          className="ml-1 inline-flex flex-shrink-0 items-center text-stone-400"
                          title={marker}
                          aria-label={marker}
                          role="img"
                          data-testid={`source-breakdown-marker-${source}`}
                        >
                          <Info className="h-3 w-3" strokeWidth={2.5} />
                        </span>
                      ) : null}
                    </div>
                    <span className="text-sm font-semibold text-stone-900 ml-3 flex-shrink-0">
                      {formatValue(value)}
                    </span>
                  </div>
                  <div className="h-1 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-stone-400 rounded-full transition-all duration-500"
                      style={{ width: `${max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0}%` }}
                    />
                  </div>
                </div>
              </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};

export default SourceBreakdown;
